import type {
  InventoryAvailability,
  InventoryFamily,
  InventoryItem,
  ManualReasonCode,
  MovementEvent,
} from "../api/types";

/**
 * What the backend's vocabulary is called on screen.
 *
 * Labels only. Nothing here decides anything — availability is computed by the
 * server from the item's own threshold, and a second opinion about what "low"
 * means is exactly how two screens come to disagree about the same number.
 */

export const AVAILABILITY_LABELS: Record<InventoryAvailability, string> = {
  in_stock: "In stock",
  low_stock: "Low stock",
  out_of_stock: "Out of stock",
};

export const FAMILY_LABELS: Record<InventoryFamily, string> = {
  glasses: "Glasses",
  contact_lenses: "Contact lenses",
  accessories: "Accessories",
};

/**
 * The variant an operator is actually being shown — or nothing at all.
 *
 * THE ONE PLACE INVENTORY DECIDES THIS. The table cell, both dialogs and the
 * accessible names those dialogs are reached by all ask here, so no two
 * surfaces can come to disagree about whether the thing in front of an operator
 * has a variant. A conditional written again in a component is how the Adjust
 * dialog came to announce a name the table had already refused to print.
 *
 * `null` means there is no business variant to show. An accessory that comes
 * one way still needs a variant, because stock and photographs have to hang off
 * something — but nobody chose it, and its stored name is whatever the intake
 * wrote. Refusing to present it is not a rename: the row keeps its real
 * `variant_id`, and every operation still works on it.
 *
 * THE ANSWER IS THE BACKEND'S, read off `variant_is_structural` and re-derived
 * nowhere. Nothing here looks at the stored name, at the swatch, or at how many
 * rows happen to be on this page — a page is twenty-five rows, and an accessory
 * whose Gold and Silver fall on either side of a boundary would look single
 * from here and lose both of its real names.
 */
export function variantLabel(item: InventoryItem): string | null {
  if (item.family === "accessories" && item.variant_is_structural) return null;
  return item.variant_name;
}

/**
 * How one row is named when it is SPOKEN about rather than tabulated: the
 * accessible name of an action, the caption over its history.
 *
 * Built from `variantLabel`, so a structural row is announced by its product
 * alone. Appending the stored name here would read it out to precisely the one
 * operator who cannot see that the Variant column says nothing.
 */
export function itemLabel(item: InventoryItem): string {
  const variant = variantLabel(item);
  return variant === null
    ? item.product_name
    : `${item.product_name} — ${variant}`;
}

export const REASON_LABELS: Record<ManualReasonCode, string> = {
  stock_received: "Stock received",
  recount: "Recount",
  damage: "Damage",
  correction: "Correction",
  other: "Other",
};

/** The order the dialog offers them in: the two an operator reaches for most,
 *  then the corrections, then the one that needs an explanation typed. */
export const REASON_ORDER: ManualReasonCode[] = [
  "stock_received",
  "recount",
  "damage",
  "correction",
  "other",
];

/**
 * One line of history, in words.
 *
 * Built from what the backend actually stored, never from arithmetic over it.
 * An order movement says which order; a manual one says which reason an
 * administrator chose; the two opening balances are spelled differently
 * because they are different claims — one is a quantity somebody entered, the
 * other is a statement about where this system's records begin.
 */
export function describeMovement(event: MovementEvent): string {
  switch (event.reason) {
    case "order_placed":
      return event.order_number ? `Order ${event.order_number}` : "Order placed";
    case "order_cancelled":
      return event.order_number
        ? `Order ${event.order_number} cancelled`
        : "Order cancelled";
    case "manual_adjustment":
      return event.manual_reason_code
        ? REASON_LABELS[event.manual_reason_code]
        : "Manual adjustment";
    case "opening_balance":
      return "Opening balance";
    case "migration_opening_balance":
      // Still spelled apart from the plain opening balance, because they are
      // different claims — one is a quantity somebody entered when the colour
      // was created, the other is where this system's records begin. What it
      // no longer says is WHO: the Who column now carries that authoritatively,
      // and repeating "inventory system" in both places said it twice.
      return "Opening balance (start of records)";
    default:
      // A reason this screen has not been taught yet. Shown as the backend
      // spells it rather than as "Unknown": an operator can search for the
      // real word, and cannot search for our guess.
      return event.reason.replace(/_/g, " ");
  }
}

/**
 * Who a movement is attributed to, in words an operator can read.
 *
 * THE ONE PLACE INVENTORY DECIDES THIS, and it decides it from the backend's
 * typed answer. Nothing here parses an actor string, because no actor string
 * arrives: `admin:<id>`, `customer` and `system:migration` are the ledger's own
 * vocabulary and stay on the server.
 *
 * A KNOWN PARTY IS NEVER DRAWN AS AN UNKNOWN ONE. A customer's own purchase is
 * somebody — the shop knows exactly who caused it and why — so rendering it as
 * a dash told an operator the ledger had lost the answer when it never had.
 * The dash is reserved for the one case that really is unaccounted for: a row
 * naming a party this shop cannot describe.
 *
 * An administrator whose display name cannot be resolved is still an
 * administrator, so they are named as one. Their id is NOT shown in their
 * place: a uuid identifies nobody to the person reading the screen.
 */
export function movementActor(event: MovementEvent): string {
  switch (event.actor_kind) {
    case "admin": {
      const name = event.actor_display_name?.trim();
      return name ? name : "Administrator";
    }
    case "customer":
      return "Customer";
    case "system":
      return "Inventory system";
    default:
      return "—";
  }
}

/** `+10` / `-2`, so a column of them reads as a ledger. */
export function formatDelta(delta: number): string {
  return delta > 0 ? `+${delta}` : String(delta);
}

/** What the threshold field says when nobody has set one.
 *
 * "Not configured" and not "5", and not "—": there is no hidden default, and
 * an operator who sees a number assumes somebody chose it. */
export function thresholdLabel(threshold: number | null): string {
  if (threshold === null) return "Not configured";
  return `${threshold} unit${threshold === 1 ? "" : "s"}`;
}
